import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Checkbox } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useFeature from 'services/features/useFeature';
import useSubscriptions from 'services/general/subscriptionsList';
import { useDownloadAllPDFs } from 'services/pdfReport/useDownloadAllPDFs';
import useRequestAccountDeletion from 'services/account/useRequestAccountDeletion';
import { getPractitionerId, getTinyAccounts } from 'store/account';
import { getKits } from 'store/shared';
import { getIsImpersonatorPartnerOrPractitioner } from 'store/user';

import {
  ActivationBackButton,
  Button,
  ButtonColors,
  ButtonSizes,
  ContainerSizes,
  PageContainer,
  Typography,
} from 'components';

import styles from './DeleteAccount.module.css';

export const ACCOUNT_DELETION_REQUEST_FLAG = 'account_deletion_request';
export const DELETE_ACCOUNT_PATH = '/account/delete';
export const DELETE_ACCOUNT_REQUESTED_PATH = '/account/delete/requested';

const listNames = (names: string[]) =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { isFeatureEnabled } = useFeature(ACCOUNT_DELETION_REQUEST_FLAG);
  const { isAnyTinyPlus } = useSubscriptions();
  const { downloadAllPDFs, isLoading: isDownloading } = useDownloadAllPDFs();
  const requestDeletion = useRequestAccountDeletion();
  const kits = useSelector(getKits);
  const tinyAccounts = useSelector(getTinyAccounts);
  const practitionerId = useSelector(getPractitionerId);
  const isImpersonatorPartnerOrPractitioner = useSelector(
    getIsImpersonatorPartnerOrPractitioner,
  );
  const [hasAcknowledgedTinyPlus, setHasAcknowledgedTinyPlus] = useState(false);

  if (!isFeatureEnabled || isImpersonatorPartnerOrPractitioner) {
    return <Navigate to='/account' replace />;
  }

  const partnerNames = [
    ...new Set(
      kits
        .map(kit => kit.partner?.name)
        .filter((name): name is string => !!name),
    ),
  ];
  const partner = listNames(partnerNames);
  const hasPractitioner =
    !!practitionerId ||
    tinyAccounts?.some(tinyAccount => !!tinyAccount.practitioner) ||
    kits.some(kit => !!kit.practitioner);
  const canSubmit = !isAnyTinyPlus || hasAcknowledgedTinyPlus;

  const goBackToAccount = () => navigate('/account');

  const submitRequest = async () => {
    try {
      await requestDeletion.mutateAsync();
      trackEvent('Account Deletion Requested');
      navigate(DELETE_ACCOUNT_REQUESTED_PATH, { replace: true });
    } catch {
      toast.error('Your request could not be sent. Please try again');
    }
  };

  return (
    <PageContainer size={ContainerSizes.SM} className={styles.page}>
      <div className={styles.back}>
        <ActivationBackButton onBack={goBackToAccount} />
      </div>
      <Typography as='h1' variant='heading-xl'>
        Before you delete your account
      </Typography>
      <div className={styles.section}>
        <div className='flex flex-column gap-1'>
          <Typography as='p' variant='body-s' weight='bold'>
            Important things to note:
          </Typography>
          <ul className={styles.list}>
            <li>
              <Typography variant='body-s'>
                You and other members on your account will lose access to all
                results.
              </Typography>
            </li>
            <li>
              <Typography variant='body-s'>
                We'll email you to get your consent to delete your data.
              </Typography>
            </li>
            {partner && (
              <li>
                <Typography variant='body-s'>
                  {`We’ll let ${partner} know that your account and data have been deleted. However, they may have downloaded copies of your results. To have those copies deleted, you’ll need to contact ${partner} directly.`}
                </Typography>
              </li>
            )}
            {hasPractitioner && (
              <li>
                <Typography variant='body-s'>
                  We’ll let your practitioners know that your account and data
                  have been deleted. However, they may have downloaded copies of
                  your results. To have those copies deleted, you’ll need to
                  contact your practitioner directly.
                </Typography>
              </li>
            )}
            <li>
              <Typography variant='body-s' weight='bold'>
                Deleting your account does not cancel your Tiny+ membership.
              </Typography>
            </li>
          </ul>
        </div>
        <hr className={styles.divider} />
        <div className={styles.download}>
          <Typography as='p' variant='body-s' weight='bold'>
            Before you proceed, we recommend downloading all PDF results for you
            and your family members first.
          </Typography>
          <Button
            label='Download all reports'
            size={ButtonSizes.S}
            color={ButtonColors.LIGHT}
            iconNameLeft='download'
            iconSize='xs'
            loading={isDownloading}
            onClick={downloadAllPDFs}
          />
        </div>
        {isAnyTinyPlus && (
          <div
            className={styles.consent}
            onClick={() => setHasAcknowledgedTinyPlus(prev => !prev)}
          >
            <Checkbox
              checked={hasAcknowledgedTinyPlus}
              onCheck={event => {
                event.stopPropagation();
                setHasAcknowledgedTinyPlus(prev => !prev);
              }}
            />
            <Typography variant='body-s' className={styles.consentLabel}>
              I understand that deleting my account does not cancel my Tiny+
              membership.
            </Typography>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <Button
          label='Delete my account'
          size={ButtonSizes.M}
          color={ButtonColors.RED}
          width='full'
          disabled={!canSubmit}
          loading={requestDeletion.isPending}
          onClick={submitRequest}
        />
        <Button
          label='Back to account'
          size={ButtonSizes.M}
          color={ButtonColors.LIGHT}
          width='full'
          onClick={goBackToAccount}
        />
      </div>
    </PageContainer>
  );
};

export default DeleteAccount;
